import TimeoutCta from '@map/components/TimeoutCta/TimeoutCta';
import { useFetchUser } from '@map/hooks/userApi';
import { useShowMessage } from '@map/state/notifications';
import { makeStyles } from '@map/theme';
import { Typography } from '@mui/material';
import useNavigate from '@shared/hooks/useNavigate';
import Button from '@ui/Button';
import LoadingIndicator from '@ui/LoadingIndicator';
import AchievedIllustration from '@ui/SVGIllustration/library/AchievedIllustration';
import AttentionIllustration from '@ui/SVGIllustration/library/AttentionIllustration';
import BlobIllustration from '@ui/SVGIllustration/library/BlobIllustration';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const useStyles = makeStyles(({ spacing, breakpoints, sizing }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    alignSelf: 'center',
    justifyContent: 'space-between',
    padding: spacing(6, 8),
  },
  blobIllustration: {
    position: 'absolute',
    top: spacing(-45),
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    zIndex: -1,
    height: sizing(150),
    [breakpoints.up('sm')]: {
      height: sizing(200),
      top: spacing(-120),
    },
    [breakpoints.up('md')]: {
      height: sizing(300),
      top: spacing(-200),
    },
  },
  illustration: {
    width: 210,
    height: 150,
    marginTop: spacing(17.5),
    [breakpoints.up('md')]: {
      width: 290,
      height: 205,
      marginTop: spacing(20),
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: spacing(6),
    gap: spacing(25),
    [breakpoints.up('md')]: {
      gap: spacing(29),
    },
  },
  text: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: spacing(4),
  },
}));

type Props = {
  to: string;
};
const EmailVerificationResult = ({ to }: Props) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const fetchUser = useFetchUser();
  const showMessage = useShowMessage();
  const [verified, setVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleContinue = useCallback(() => {
    navigate(to);
  }, [navigate, to]);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const url = `/users/confirmation${window.location.search}`;
        //hijacking handler to show green toast when already verified
        await axios.get(url, { handlerDisabled: true });
        setVerified(true);
        fetchUser({});
      } catch (err: any) {
        if (err.response?.data?.confirmed) {
          handleContinue();
          showMessage({
            type: 'success',
            // i18n-tasks-use t('registration.verify_email.already_verified_description')
            textKey: 'registration.verify_email.already_verified_description',
          });
          return;
        }
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [fetchUser, handleContinue, showMessage]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div className={classes.root}>
      <BlobIllustration className={classes.blobIllustration} />
      <div className={classes.content}>
        {verified ? (
          <AchievedIllustration className={classes.illustration} />
        ) : (
          <AttentionIllustration className={classes.illustration} />
        )}
        <div className={classes.text}>
          <Typography variant="h5" textAlign="center">
            {verified ? (
              <FormattedMessage id="registration.verify_email.email_verified_label" />
            ) : (
              <FormattedMessage id="registration.verify_email.email_verification_failed_label" />
            )}
          </Typography>
          <Typography variant="body2" textAlign="center">
            {verified ? (
              <FormattedMessage id="registration.verify_email.you_are_ready_to_continue_description" />
            ) : (
              <FormattedMessage id="registration.verify_email.please_try_again_description" />
            )}
          </Typography>
        </div>
      </div>
      <TimeoutCta seconds={6} onTimeout={handleContinue}>
        <Button onClick={handleContinue}>
          <FormattedMessage id="common.ok_cta" />
        </Button>
      </TimeoutCta>
    </div>
  );
};

export default EmailVerificationResult;
